import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import Card from "../components/Card";
import CustomDropdown from "./CustomDropdown";


export default function RegisteredByDayChart({ user }) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth(); // 0-based (Jan = 0)

  const [month, setMonth] = useState("");
  const [year, setYear] = useState(currentYear);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Determine API based on role
  const getApiUrl = () => {
    if (!user?.role || !user?.id) return null;

    switch (user.role) {
      case 3: // CNF → show SuperStockists
        return `https://goelectronix.in/api/v1/org_user?role=4&cnf_id=${user.id}&page=1&per_page=1000`;
      case 4: // SuperStockist → show Distributors
        return `https://goelectronix.in/api/v1/org_user?role=5&ss_id=${user.id}&page=1&per_page=1000`;
      case 5: // Distributor → show Retailers
        return `https://goelectronix.in/api/v1/org_user?role=7&dist_id=${user.id}&page=1&per_page=1000`;
      default: // Retailer or others → show their customers
        return `https://goelectronix.in/api/v1/get-customers?retailer_id=${user.id}&page=1&per_page=1000&login_id=${user.id}`;
    }
  };

  // ✅ Dynamic months logic
  const monthOptions = useMemo(() => {
    const allMonths = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    if (year === 2025) {
      if (currentYear === 2025) return allMonths.slice(0, currentMonthIndex + 1);
      else if (currentYear > 2025) return allMonths.slice(2, 12);
    }

    if (year === currentYear) return allMonths.slice(0, currentMonthIndex + 1);
    return allMonths;
  }, [year, currentMonthIndex, currentYear]);

  // ✅ Dynamic years logic
  const yearOptions = useMemo(() => {
    if (currentYear === 2025) return [2025];
    if (currentYear > 2025) return [2025, currentYear];
    return [currentYear];
  }, [currentYear]);

  // ✅ Default selected month
  useEffect(() => {
    const currentMonthName = new Date().toLocaleString("default", { month: "long" });
    if (monthOptions.includes(currentMonthName)) setMonth(currentMonthName);
    else setMonth(monthOptions[0]);
  }, [monthOptions]);

  // ✅ Fetch chart data
  const fetchChartData = async () => {
    const apiUrl = getApiUrl();
    if (!apiUrl) return;

    setLoading(true);
    try {
      const res = await axios.get(apiUrl);
      const apiData = res.data?.data || [];

      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      const allDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

      if (user.role === 7) {
        // ✅ Retailer: Customer Registration Logic (previous logic)
        const dataMap = apiData.reduce((acc, item) => {
          const createdAt = item?.created_at ? new Date(item.created_at) : null;
          if (!createdAt) return acc;
          if (createdAt.getMonth() !== monthIndex || createdAt.getFullYear() !== year)
            return acc;

          const dayNum = createdAt.getDate();
          if (!acc[dayNum]) acc[dayNum] = { rec: 0, chalte: 0 };

          if (item.customer_type === 1) acc[dayNum].rec += 1;
          if (item.customer_type === 3) acc[dayNum].chalte += 1;

          return acc;
        }, {});

        const formatted = allDays.map((day) => ({
          day,
          rec: dataMap[day]?.rec || 0,
          chalte: dataMap[day]?.chalte || 0,
        }));

        setChartData(formatted);
      } else {
        // ✅ CNF / SuperStockist / Distributor: Activation count logic
        const dataMap = apiData.reduce((acc, item) => {
          const createdAt = item?.created_at ? new Date(item.created_at) : null;
          if (!createdAt) return acc;
          if (createdAt.getMonth() !== monthIndex || createdAt.getFullYear() !== year)
            return acc;

          const dayNum = createdAt.getDate();
          if (!acc[dayNum]) acc[dayNum] = 0;
          acc[dayNum] += 1;
          return acc;
        }, {});

        const formatted = allDays.map((day) => ({
          day,
          activations: dataMap[day] || 0,
        }));

        setChartData(formatted);
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (month) fetchChartData();
  }, [user, month, year]);

  // ✅ Label text based on role
  const getChartLabel = () => {
    if (user.role === 3) return "Super Stockist Activations";
    if (user.role === 4) return "Distributor Activations";
    if (user.role === 5) return "Retailer Activations";
    if (user.role === 7) return "Customer Registrations";
    return "Activations";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
    >
      <Card className="rounded-2xl border-slate-200/70 shadow-md">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
          <div>
            <h4 className="text-base font-semibold">Registered by Day</h4>
            <p className="text-xs text-slate-500">{getChartLabel()}</p>
          </div>
          <div className="flex items-center gap-3">
            <CustomDropdown
              label="Select Year"
              items={yearOptions}
              selected={year}
              onSelect={setYear}
            />
            <CustomDropdown
              label="Select Month"
              items={monthOptions}
              selected={month}
              onSelect={setMonth}
            />
          </div>
        </div>

        <div className="h-72 w-full overflow-x-auto overflow-y-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
              <p className="text-sm">Loading chart...</p>
            </div>
          ) : (
            <div className="min-w-[800px]" style={{ width: `${chartData.length * 10}px` }}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} barCategoryGap="10%" barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {user.role === 7 ? (
                    <>
                      <Bar
                        dataKey="rec"
                        name="Recommended"
                        radius={[6, 6, 0, 0]}
                        fill="#3b82f6"
                      />
                      <Bar
                        dataKey="chalte"
                        name="Chalte Chalte"
                        radius={[6, 6, 0, 0]}
                        fill="#818cf8"
                      />
                    </>
                  ) : (
                    <Bar
                      dataKey="activations"
                      name="Activations"
                      radius={[6, 6, 0, 0]}
                      fill="#3b82f6"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}